import Content, { ContentNS } from "content/content";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import CountryCodePicker from "features/shared/components/picker/CountryCodePicker";
import NationalityPicker from "features/shared/components/picker/NationalityPicker";
import DatePicker from "features/shared/components/date-picker/DatePicker";
import RadioGroup from "features/shared/components/radio-input/RadioGroup";
import { TextInput } from "features/shared/components/text-input/TextInput";
import useConversionAnalytics from "features/shared/hooks/analytics/useConversionAnalytics";
import useMutatedPagePath from "features/shared/hooks/analytics/useMutatedPagePath";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import { SmallPrint } from "features/shared/styles/typography/SmallPrint.styled";
import Logging from "logging";
import { GenderFromLetter } from "models/documents/gender";
import InternationalPassport, {
  PassportFieldName,
} from "models/documents/international-passport";
import { FC, useEffect, useRef, useState } from "react";
import DataProvider from "services/data-provider/data-provider";
import { DocProcessingStatus } from "models/doc-processing-status";
import { useAppStore } from "store/store";
import { getDateWithoutTime, LocalDate } from "utils";
import { useShallow } from "zustand/shallow";

import useGenderOptions from "./hooks/useGenderOptions";
import useScrollToFirstIssue from "./hooks/useScrollToFirstIssue";
import StyledPassportForm from "./styles/StyledPassportForm.styled";
import { elementIdFromPassportFieldName } from "./utils/utils";
import validateForm from "./validate";
import {
  ApplyDocumentErrorWrapper,
  ApplyDocumentErrorIdMismatch,
  ApplyDocumentErrorRulesMismatch,
  ApplyDocumentErrorSignalsMismatch,
} from "services/data-provider/ApplyDocumentResponse";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import PassportType, {
  PassportTypeToCode,
} from "models/documents/passport-type";
import FromListPicker from "features/shared/components/picker/FromListPicker";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import ChecklistItem from "models/checklist/checklist-item/checklist-item";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import { useSearchParams } from "react-router-dom";
import { getGeneralAnalyticsParams } from "features/shared/hooks/analytics/useGeneralAnalytics";
import { getPaxLevelParams } from "features/shared/hooks/analytics/usePaxLevelAnalytics";
import AnalyticsService from "services/analytics/analytics";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import SessionIneligibilityReason, {
  SessionIneligibilityReasonFromApi,
} from "models/session-ineligibility-reason";

const mapSignalsFieldToFormField = (field: string): string => {
  switch (field) {
    case "country_code":
      return "countryCode";
    case "expiry_date":
      return "expiryDate";
    case "given_name":
      return "givenName";
    case "sex":
      return "gender";
    case "passport_type":
      return "passportType";
    default:
      return field;
  }
};

interface PassportFormContentProps {
  itemId: string | undefined;
}

interface PassportFormByItemProps {
  item: ChecklistItem;
}

const PassportFormContent: FC<PassportFormContentProps> = ({ itemId }) => {
  const locale = useAppStore((state) => state.locale);

  const textPassport = (s: string) =>
    Content.shared.t(s, { ns: ContentNS.passport });

  const navigate = useNavigateWithQuery();

  const [path, title, location] = useMutatedPagePath();
  const [isLoading, setIsLoading] = useState(false);

  const paxSessions = useAppStore((state) => state.paxSessions);
  const journey = useAppStore((state) => state.journey);

  const paxSession = usePaxSession();
  const paxSessionId = paxSession?.paxSessionId || "missing";

  const formRef = useRef<HTMLFormElement | null>(null);

  const sendConversionEvents = useConversionAnalytics();

  const genderOptions = useGenderOptions();

  const passportTypeOptions = (
    Object.keys(PassportType) as Array<keyof typeof PassportType>
  ).map((key) => {
    return {
      id: PassportTypeToCode(PassportType[key]),
      value: textPassport("passport-type-" + key.toLocaleLowerCase()),
    };
  });

  const [
    values,
    errors,
    changeValue,
    setError,
    sessionId,
    setProcessingStatus,
    hasSignalsMismatch,
    setHasSignalsMismatch,
    setHasBookingMismatch,
    resetErrors,
    setProcessedFileName,
    setProcessingMayTakeLong,
    processingError,
    setIneligibilityReason,
    setSessionIsLocked,
  ] = useAppStore(
    useShallow((state) => [
      state.passportValues,
      state.passportErrors,
      state.changePassportValue,
      state.setPassportError,
      state.sessionId,
      state.setProcessingStatus,
      state.hasSignalsMismatch,
      state.setHasSignalsMismatch,
      state.setHasManualBookingMismatch,
      state.resetPassportErrors,
      state.setProcessedFileName,
      state.setProcessingMayTakeLong,
      state.processingError,
      state.setIneligibilityReason,
      state.setSessionIsLocked,
    ])
  );

  // delay function
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const analyticsParams = {
    ...getGeneralAnalyticsParams(
      paxSessions,
      journey,
      path,
      title,
      location,
      locale
    ),
    ...getPaxLevelParams(paxSessions, paxSession?.paxSessionId || null),
    item_type: ChecklistItemType.TravelDoc,
    is_scan: false,
    is_upload: false,
    is_manual: true,
  };

  useScrollToFirstIssue(errors, processingError, formRef);

  useEffect(() => {
    // send once at render
    AnalyticsService.shared.trackEvent(
      "manual_selected",
      analyticsParams,
      false // isScanRelated
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFormSubmit = async () => {
    if (isLoading) {
      return;
    }

    const formValid = validateForm(values, setError, {
      path,
      title,
      location,
    });

    if (!formValid) {
      // if form is not valid, we show field validation errors, not the passport
      // mismatch errors
      setHasSignalsMismatch(false);

      return;
    }

    setIsLoading(true);
    resetErrors();

    const passport: InternationalPassport = {
      number: values.number,
      givenName: values.givenName,
      surname: values.surname,
      countryCode: values.countryCode,
      nationality: values.nationality,
      birthdate: LocalDate.fromIsoString(values.birthdate),
      expiryDate: LocalDate.fromIsoString(values.expiryDate),
      gender: GenderFromLetter(values.gender),
      passportType: values.passportType,
    };

    if (hasSignalsMismatch) {
      resetErrors();
    }

    setProcessingStatus(DocProcessingStatus.InProgress, null);
    setProcessedFileName(null);
    setProcessingMayTakeLong(null);
    navigate("../processing");

    AnalyticsService.shared.trackEvent(
      "apply_started",
      analyticsParams,
      false // isScanRelated
    );

    DataProvider.shared
      .applyPassport(sessionId, {
        paxSessionId,
        passport: passport,
        ignoreMistakes: hasSignalsMismatch,
        itemId: itemId,
      })
      .then(async (res) => {
        // success
        setHasSignalsMismatch(false);

        const previousPaxSessions = useAppStore.getState().paxSessions;

        if (res.updatedPaxSession) {
          useAppStore.getState().updatePaxSession(res.updatedPaxSession);
        }

        if (!res.successfullyVerified) {
          // verification required
          setProcessingStatus(
            DocProcessingStatus.Error,
            "verification_required"
          );

          return;
        }

        setProcessingStatus(DocProcessingStatus.Success, null);

        AnalyticsService.shared.trackEvent(
          "apply_success",
          analyticsParams,
          false // isScanRelated
        );

        // wait 100ms just in case - to increase chances state
        // indeed was updated
        await delay(100);

        sendConversionEvents(
          previousPaxSessions,
          useAppStore.getState().paxSessions
        );
      })
      .catch((err) => {
        if (err instanceof ApplyDocumentErrorWrapper) {
          // apply document error

          if (err.error instanceof ApplyDocumentErrorSignalsMismatch) {
            // signals mismatch
            AnalyticsService.shared.trackEvent(
              "form_mismatch",
              {
                ...analyticsParams,
                mismatch_fields_number: err.error.fields.length,
              },
              false // isScanRelated
            );

            err.error.fields.forEach((field) => {
              setError(
                mapSignalsFieldToFormField(field),
                textPassport("error.signals-mismatch")
              );
            });

            setHasSignalsMismatch(true);
            setProcessingStatus(DocProcessingStatus.Error, "signals_mismatch");
            return;
          }

          if (err.error instanceof ApplyDocumentErrorIdMismatch) {
            // id mismatch
            const idMismatches = err.error.idMismatches;
            Object.keys(idMismatches).forEach((field) => {
              if ((idMismatches as any)[field]) {
                setError(field, textPassport("error.booking-mismatch"));
              }
            });

            setHasBookingMismatch(true);
            setProcessingStatus(DocProcessingStatus.Error, err.error);

            AnalyticsService.shared.trackEvent(
              "apply_failed",
              {
                ...analyticsParams,
                failed_to_process: 0,
                id_mismatch_error: 1,
                rules_mismatch_error: 0,
              },
              false // isScanRelated
            );

            return;
          }

          if (err.error instanceof ApplyDocumentErrorRulesMismatch) {
            // rules mismatch
            setProcessingStatus(DocProcessingStatus.Error, err.error);
            return;
          }

          // some other api error
          setProcessingStatus(DocProcessingStatus.Error, err.error);

          return;
        }

        if (
          err instanceof DataProviderGeneralException &&
          err.type !== DataProviderExceptionType.Unknown &&
          err.type !== DataProviderExceptionType.BadRequest &&
          err.type !== DataProviderExceptionType.NotFound
        ) {
          // api "general" error
          switch (err.type) {
            case DataProviderExceptionType.NotProcessable:
            case DataProviderExceptionType.NotEligible:
              // not processable or not eligible
              setIneligibilityReason(
                SessionIneligibilityReasonFromApi(err.message)
              );
              setProcessingStatus(DocProcessingStatus.Ineligible, null);

              // ineligible
              AnalyticsService.shared.trackEvent(
                "apply_ineligible",
                {
                  ...analyticsParams,
                  failed_to_process: 1,
                  id_mismatch_error: 0,
                  rules_mismatch_error: 0,
                },
                false // isScanRelated
              );
              return;

            case DataProviderExceptionType.SessionIsFinalised:
              setIneligibilityReason(
                SessionIneligibilityReason.SessionIsFinalised
              );
              return;

            case DataProviderExceptionType.SessionIsLocked:
              setSessionIsLocked(true);
              return;
          }
        }

        Logging.log(
          "passport-form",
          paxSessionId,
          `Failed to apply manual passport: ${
            err instanceof Error ? err.message : err
          }`
        );
        setProcessingStatus(DocProcessingStatus.Error, "unknown_error");

        AnalyticsService.shared.trackEvent(
          "apply_failed",
          {
            ...analyticsParams,
            failed_to_process: 1,
            id_mismatch_error: 0,
            rules_mismatch_error: 0,
          },
          false // isScanRelated
        );
      });
  };

  const onFieldFocus = (field: PassportFieldName) => () => {
    AnalyticsService.shared.trackEvent(
      "form_touched",
      {
        ...analyticsParams,
        field: field,
      },
      false // isScanRelated
    );
  };

  return (
    <StyledPassportForm>
      <form ref={formRef}>
        <TextInput
          id={elementIdFromPassportFieldName(PassportFieldName.number)}
          value={values.number}
          maxLength={10}
          label={textPassport("passport-number")}
          error={errors.number}
          onChange={changeValue(PassportFieldName.number)}
          onFocus={onFieldFocus(PassportFieldName.number)}
        />
        <TextInput
          id={elementIdFromPassportFieldName(PassportFieldName.givenName)}
          value={values.givenName}
          label={textPassport("given-name")}
          error={errors.givenName}
          onChange={changeValue(PassportFieldName.givenName)}
          onFocus={onFieldFocus(PassportFieldName.givenName)}
        />
        <TextInput
          id={elementIdFromPassportFieldName(PassportFieldName.surname)}
          value={values.surname}
          label={textPassport("surname")}
          error={errors.surname}
          onChange={changeValue(PassportFieldName.surname)}
          onFocus={onFieldFocus(PassportFieldName.surname)}
        />
        <CountryCodePicker
          id={elementIdFromPassportFieldName(PassportFieldName.countryCode)}
          label={textPassport("country-code")}
          value={values.countryCode}
          error={errors.countryCode}
          onChange={changeValue(PassportFieldName.countryCode)}
          placeholder={textPassport("select-country")}
          onFocus={onFieldFocus(PassportFieldName.countryCode)}
        />
        <NationalityPicker
          id={elementIdFromPassportFieldName(PassportFieldName.nationality)}
          label={textPassport("nationality")}
          value={values.nationality}
          error={errors.nationality}
          onChange={changeValue(PassportFieldName.nationality)}
          placeholder={textPassport("select-country")}
          onFocus={onFieldFocus(PassportFieldName.nationality)}
        />

        <RadioGroup
          id={elementIdFromPassportFieldName(PassportFieldName.gender)}
          error={errors.gender}
          label={<SmallPrint>{textPassport("gender")}</SmallPrint>}
          options={genderOptions}
          name={"gender"}
          onChange={changeValue(PassportFieldName.gender)}
          onFocus={onFieldFocus(PassportFieldName.gender)}
          value={values.gender}
        />

        <DatePicker
          id={elementIdFromPassportFieldName(PassportFieldName.birthdate)}
          value={values.birthdate}
          label={textPassport("birthdate")}
          error={errors.birthdate}
          onChange={changeValue(PassportFieldName.birthdate)}
          onFocus={() => {
            onFieldFocus(PassportFieldName.birthdate);
          }}
          max={getDateWithoutTime(new Date())}
        />
        <DatePicker
          id={elementIdFromPassportFieldName(PassportFieldName.expiryDate)}
          value={values.expiryDate}
          label={textPassport("expiry-date")}
          error={errors.expiryDate}
          onChange={changeValue(PassportFieldName.expiryDate)}
          onFocus={onFieldFocus(PassportFieldName.expiryDate)}
          max="2070-01-01"
        />

        <FromListPicker
          id={elementIdFromPassportFieldName(PassportFieldName.passportType)}
          label={textPassport("passport-type")}
          options={passportTypeOptions}
          value={values.passportType}
          error={errors.passportType}
          onChange={changeValue(PassportFieldName.passportType)}
          onFocus={onFieldFocus(PassportFieldName.passportType)}
        />
      </form>
      <div>
        <SubmitButton onClick={onFormSubmit}>
          {hasSignalsMismatch
            ? textPassport("submit-anyway")
            : textPassport("submit")}
        </SubmitButton>
      </div>
    </StyledPassportForm>
  );
};

const PassportFormByItem: FC<PassportFormByItemProps> = ({ item }) => {
  return <PassportFormContent itemId={item.id} />;
};

const PassportFormByType: FC = () => {
  return <PassportFormContent itemId={undefined} />;
};

const PassportForm: FC = () => {
  useAppStore((state) => state.locale);

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();

  if (itemLevelNavigationState instanceof ItemNavigationState) {
    // itemId
    return <PassportFormByItem item={itemLevelNavigationState.item} />;
  }

  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    // itemType
    return <PassportFormByType />;
  }

  // neither itemId or itemType - should not be the case
  throw new ItemLevelNavigationError(
    sessionId,
    paxSession?.paxSessionId,
    `neither itemId or itemType navigation state, which is odd`
  );
};

export default PassportForm;
